import {transactionFingerprint} from './transaction-fingerprint';
import {
  NookDatabase,
  assertWritable,
  markSearchDirty,
} from "../infrastructure/database/database";
import { assertCapacity } from "../infrastructure/backup/snapshot";
import {
  studySchemas,
  studyDraftSchemas,
  studyDraftSchema,
  studyRefs,
  type StudyItem,
  type StudyKind,
  type StudyPlan,
  type StudyRevision,
} from "../domain/study/model";
import { taskSchema, blankItem, type Task } from "../domain/items/model";
import { collectAttachments } from "./attachment-gc";
import { clearProjectNextForTask } from "./item-service";
export async function studyReferenceExists(
  db: NookDatabase,
  type: "resource" | "task" | "plan",
  id: string,
) {
  const rows: StudyItem[] = [
    ...(await db.manualStudyPlans.toArray()),
    ...(await db.manualStudySessions.toArray()),
    ...(await db.manualStudyDrafts.toArray()).map((d) => d.payload),
    ...(await db.revisions.toArray())
      .filter(
        (r): r is StudyRevision =>
          "entityType" in r &&
          ["study-plan", "study-session"].includes(r.entityType),
      )
      .map((r) => r.snapshot),
  ];
  return rows.some((v) => {
    const r = studyRefs(v);
    return type === "plan"
      ? r.plan === id
      : (type === "resource" ? r.resources : r.tasks).includes(id);
  });
}
export class StudyService {
  constructor(readonly db: NookDatabase) {}
  table(kind: StudyKind) {
    return this.db.table<StudyItem, string>(
      kind === "study-plan" ? "manualStudyPlans" : "manualStudySessions",
    );
  }
  private async command<T>(
    op: string,
    input: unknown,
    run: () => Promise<{ value: T; id: string; revision: number }>,
    duplicate: (id: string) => Promise<T>,
  ) {
    const fingerprint = await transactionFingerprint(input);
    return this.db.transaction("rw", this.db.tables, async () => {
      await assertWritable(this.db);
      const previous = await this.db.operations.get(op);
      if (previous) {
        if (
          previous.kind !== "study-command" ||
          previous.fingerprint !== fingerprint
        )
          throw Error("操作标识已用于不同输入");
        return duplicate(previous.resultId);
      }
      const r = await run();
      await this.db.operations.add({
        id: op,
        kind: "study-command",
        fingerprint,
        resultId: r.id,
        resultRevision: r.revision,
        committedAt: new Date().toISOString(),
      });
      await markSearchDirty(this.db);
      await assertCapacity(this.db);
      return r.value;
    });
  }
  private async history(
    kind: StudyKind,
    item: StudyItem,
    reason: StudyRevision["reason"],
  ) {
    await this.db.revisions.add({
      id: `${kind}:${item.id}:${item.revision}`,
      entityType: kind,
      entityId: item.id,
      revision: item.revision,
      reason,
      recordedAt: item.updatedAt,
      snapshot: item,
    });
  }
  private async references(item: StudyItem, previous?: StudyItem) {
    const r = studyRefs(item),
      old = previous
        ? studyRefs(previous)
        : { resources: [], tasks: [], plan: null };
    for (const key of r.resources) {
      const v = await this.db.resources.get(key);
      if (!v || (v.deletedAt && !old.resources.includes(key)))
        throw Error("资料不存在或已在回收站");
    }
    for (const key of r.tasks) {
      const v = await this.db.tasks.get(key);
      if (!v || (v.deletedAt && !old.tasks.includes(key)))
        throw Error("Task 不存在或已在回收站");
    }
    if (r.plan) {
      const p = await this.db.manualStudyPlans.get(r.plan);
      if (!p || (p.deletedAt && old.plan !== r.plan))
        throw Error("计划不存在或已在回收站");
    }
    for (const key of item.attachmentIds)
      if (!(await this.db.attachments.get(key))) throw Error("图片引用缺失");
  }
  async draft(
    kind: StudyKind,
    input: StudyItem,
    baseRevision: number,
    id: string,
    expected: number,
    op = crypto.randomUUID(),
  ) {
    return this.command(
      op,
      { kind, input, baseRevision, id, expected },
      async () => {
        const parsed = studyDraftSchemas[kind].parse(input) as StudyItem;
        if (kind === "study-session") parsed.state = "draft";
        const target = await this.table(kind).get(input.id);
        if (
          baseRevision &&
          (!target || target.deletedAt || baseRevision > target.revision)
        )
          throw Error("草稿原对象缺失或基线来自未来");
        await this.references(parsed, target);
        const old = await this.db.manualStudyDrafts.get(id);
        if (old && (old.targetId !== input.id || old.kind !== kind))
          throw Error("草稿归属不同");
        const fork = !!old?.deletedAt || (old?.revision ?? 0) !== expected,
          now = new Date().toISOString();
        const next = studyDraftSchema.parse({
          id: fork ? crypto.randomUUID() : id,
          targetId: input.id,
          kind,
          baseRevision,
          revision: fork ? 1 : expected + 1,
          createdAt: fork ? now : (old?.createdAt ?? now),
          updatedAt: now,
          deletedAt: null,
          payload: parsed,
        });
        await this.db.manualStudyDrafts.put(next);
        return { value: next, id: next.id, revision: next.revision };
      },
      async (id) => (await this.db.manualStudyDrafts.get(id))!,
    );
  }
  async publish(draftId: string, revision: number, op: string, asCopy = false) {
    return this.command(
      op,
      { draftId, revision, asCopy },
      async () => {
        const d = await this.db.manualStudyDrafts.get(draftId);
        if (!d || d.deletedAt || d.revision !== revision)
          throw Error("草稿已变化");
        const old = asCopy
          ? undefined
          : await this.table(d.kind).get(d.targetId);
        if (old?.deletedAt) throw Error("请先恢复回收站对象");
        if (!asCopy && (old?.revision ?? 0) !== d.baseRevision)
          throw Error("另一处已有新版本；草稿保留，未覆盖");
        const key = asCopy ? crypto.randomUUID() : d.targetId;
        if (!old)
          for (const table of [
            "captures",
            "tasks",
            "events",
            "projects",
            "resources",
            "decisions",
            "diaryEntries",
            "sopTemplates",
            "sopRuns",
            "manualStudyPlans",
            "manualStudySessions",
          ])
            if (await this.db.table(table).get(key))
              throw Error("稳定 ID 已被另一对象使用");
        const now = new Date().toISOString();
        let input = {
          ...d.payload,
          id: key,
          createdAt: old?.createdAt ?? now,
          updatedAt: now,
          revision: (old?.revision ?? 0) + 1,
        };
        if (d.kind === "study-plan") {
          const previous = old as StudyPlan | undefined;
          input = {
            ...input,
            state: previous?.state ?? "active",
            endedAt: previous?.endedAt ?? null,
            result: previous?.result ?? "",
            actionResolutions: previous?.actionResolutions ?? [],
          } as StudyItem;
          if (
            previous?.state === "completed" &&
            "taskIds" in input &&
            input.taskIds.some((id) => !previous.taskIds.includes(id))
          )
            throw Error("已结束计划不能新增关联行动");
        } else input = { ...input, state: "recorded" } as StudyItem;
        const item = studySchemas[d.kind].parse(input) as StudyItem;
        await this.references(item, old);
        await this.table(d.kind).put(item);
        await this.history(d.kind, item, old ? "edited" : "created");
        await this.db.manualStudyDrafts.delete(d.id);
        return { value: item, id: key, revision: item.revision };
      },
      async (id) => {
        const p = await this.db.manualStudyPlans.get(id);
        return p ?? (await this.db.manualStudySessions.get(id))!;
      },
    );
  }
  async linkSession(
    id: string,
    revision: number,
    planId: string | null,
    op: string,
  ) {
    return this.command(
      op,
      { id, revision, planId },
      async () => {
        const old = await this.db.manualStudySessions.get(id);
        if (!old || old.deletedAt || old.revision !== revision)
          throw Error("记录已变化");
        const next = {
          ...old,
          planId,
          revision: revision + 1,
          updatedAt: new Date().toISOString(),
        };
        await this.references(next, old);
        await this.db.manualStudySessions.put(next);
        await this.history("study-session", next, "edited");
        return { value: next, id, revision: next.revision };
      },
      async (id) => (await this.db.manualStudySessions.get(id))!,
    );
  }
  async planState(
    id: string,
    revision: number,
    action: "pause" | "resume",
    op: string,
  ) {
    return this.command(
      op,
      { id, revision, action },
      async () => {
        const p = await this.db.manualStudyPlans.get(id);
        if (
          !p ||
          p.deletedAt ||
          p.revision !== revision ||
          p.state !== (action === "pause" ? "active" : "paused")
        )
          throw Error("计划状态已变化");
        const next = {
          ...p,
          state: action === "pause" ? ("paused" as const) : ("active" as const),
          revision: revision + 1,
          updatedAt: new Date().toISOString(),
        };
        await this.db.manualStudyPlans.put(next);
        await this.history(
          "study-plan",
          next,
          action === "pause" ? "paused" : "resumed",
        );
        return { value: next, id, revision: next.revision };
      },
      async (id) => (await this.db.manualStudyPlans.get(id))!,
    );
  }

  async linkTask(
    planId: string,
    revision: number,
    taskId: string | null,
    op: string,
  ) {
    return this.command(
      op,
      { planId, revision, taskId },
      async () => {
        const p = await this.db.manualStudyPlans.get(planId);
        if (
          !p ||
          p.deletedAt ||
          p.revision !== revision ||
          p.state === "completed"
        )
          throw Error("计划已变化或已结束");
        const next = studyPlanSchemaParse({
          ...p,
          taskIds: taskId ? [...new Set([...p.taskIds, taskId])] : p.taskIds,
          nextTaskId: taskId,
          revision: revision + 1,
          updatedAt: new Date().toISOString(),
        });
        await this.references(next, p);
        await this.db.manualStudyPlans.put(next);
        await this.history("study-plan", next, "edited");
        return { value: next, id: planId, revision: next.revision };
      },
      async (id) => (await this.db.manualStudyPlans.get(id))!,
    );
  }
  async createTask(
    planId: string,
    revision: number,
    title: string,
    plannedDate: string | null,
    op: string,
  ) {
    return this.command(
      op,
      { planId, revision, title, plannedDate },
      async () => {
        const p = await this.db.manualStudyPlans.get(planId);
        if (
          !p ||
          p.deletedAt ||
          p.revision !== revision ||
          p.state === "completed"
        )
          throw Error("计划已变化或已结束");
        const t = taskSchema.parse({
          ...blankItem("task"),
          title,
          plannedDate,
        });
        await this.db.tasks.add(t);
        await this.db.revisions.add({
          id: `task:${t.id}:1`,
          entityType: "task",
          entityId: t.id,
          revision: 1,
          reason: "created",
          recordedAt: t.updatedAt,
          snapshot: t,
        });
        const next = studyPlanSchemaParse({
          ...p,
          taskIds: [...p.taskIds, t.id],
          nextTaskId: t.id,
          revision: revision + 1,
          updatedAt: new Date().toISOString(),
        });
        await this.db.manualStudyPlans.put(next);
        await this.history("study-plan", next, "edited");
        return { value: t, id: t.id, revision: 1 };
      },
      async (id) => (await this.db.tasks.get(id))!,
    );
  }
  async endPlan(
    id: string,
    revision: number,
    result: string,
    choices: Record<
      string,
      { action: "keep" | "cancel" | "unlink"; revision: number }
    >,
    op: string,
  ) {
    return this.command(
      op,
      { id, revision, result, choices },
      async () => {
        const p = await this.db.manualStudyPlans.get(id);
        if (
          !p ||
          p.deletedAt ||
          p.revision !== revision ||
          p.state === "completed"
        )
          throw Error("计划已变化或已结束");
        const tasks = (await this.db.tasks.bulkGet(p.taskIds)).filter(
          (t): t is Task =>
            !!t &&
            !t.deletedAt &&
            !["completed", "cancelled"].includes(t.state),
        );
        if (
          Object.keys(choices).length !== tasks.length ||
          tasks.some(
            (t) =>
              !choices[t.id] ||
              choices[t.id].revision !== t.revision ||
              !["keep", "cancel", "unlink"].includes(choices[t.id].action),
          )
        )
          throw Error("请逐项明确处理仍未结束的行动；行动变化后需重新确认");
        const now = new Date().toISOString();
        for (const t of tasks)
          if (choices[t.id].action === "cancel") {
            const next = taskSchema.parse({
              ...t,
              state: "cancelled",
              revision: t.revision + 1,
              updatedAt: now,
            });
            await this.db.tasks.put(next);
            await this.db.revisions.add({
              id: `task:${t.id}:${next.revision}`,
              entityType: "task",
              entityId: t.id,
              revision: next.revision,
              reason: "edited",
              recordedAt: now,
              snapshot: next,
            });
            await clearProjectNextForTask(this.db, next);
          }
        const next = studyPlanSchemaParse({
          ...p,
          state: "completed",
          endedAt: now,
          result,
          nextTaskId: null,
          taskIds: p.taskIds.filter((key) => choices[key]?.action !== "unlink"),
          actionResolutions: tasks.map((t) => ({
            taskId: t.id,
            action: choices[t.id].action,
            resolvedAt: now,
          })),
          revision: revision + 1,
          updatedAt: now,
        });
        await this.db.manualStudyPlans.put(next);
        await this.history("study-plan", next, "ended");
        return { value: next, id, revision: next.revision };
      },
      async (id) => (await this.db.manualStudyPlans.get(id))!,
    );
  }
  async lifecycle(
    kind: StudyKind | "study-draft",
    id: string,
    revision: number,
    action: "archive" | "unarchive" | "trash" | "restore" | "purge",
    op: string,
  ) {
    return this.command(
      op,
      { kind, id, revision, action },
      async () => {
        if (kind === "study-draft") {
          const d = await this.db.manualStudyDrafts.get(id);
          if (!d || d.revision !== revision) throw Error("草稿已变化");
          if (["archive", "unarchive"].includes(action))
            throw Error("草稿尚未发布");
          if (action === "purge") {
            if (!d.deletedAt) throw Error("请先移入回收站");
            await this.db.manualStudyDrafts.delete(id);
            await collectAttachments(this.db);
          } else {
            if ((action === "trash") === !!d.deletedAt)
              throw Error("草稿状态已变化");
            await this.db.manualStudyDrafts.put({
              ...d,
              deletedAt: action === "trash" ? new Date().toISOString() : null,
              revision: revision + 1,
              updatedAt: new Date().toISOString(),
            });
          }
          return { value: undefined, id, revision: revision + 1 };
        }
        const old = await this.table(kind).get(id);
        if (!old || old.revision !== revision) throw Error("对象已变化");
        if (action === "purge") {
          if (!old.deletedAt) throw Error("请先移入回收站");
          if (
            await this.db.manualStudyDrafts.where("targetId").equals(id).count()
          )
            throw Error("仍有编辑草稿");
          if (
            kind === "study-plan" &&
            (await studyReferenceExists(this.db, "plan", id))
          )
            throw Error("计划仍被学习记录、草稿或历史引用；历史记录未删除");
          await this.table(kind).delete(id);
          await this.db.revisions
            .where("[entityType+entityId]")
            .equals([kind, id])
            .delete();
          await collectAttachments(this.db);
        } else {
          if (
            (old.deletedAt && action !== "restore") ||
            (!old.deletedAt && action === "restore")
          )
            throw Error("对象状态已变化");
          const now = new Date().toISOString(),
            next = {
              ...old,
              ...(["archive", "unarchive"].includes(action)
                ? { archivedAt: action === "archive" ? now : null }
                : { deletedAt: action === "trash" ? now : null }),
              revision: revision + 1,
              updatedAt: now,
            };
          await this.table(kind).put(next);
          await this.history(
            kind,
            next,
            (
              {
                archive: "archived",
                unarchive: "unarchived",
                trash: "trashed",
                restore: "restored",
              } as const
            )[action],
          );
        }
        return { value: undefined, id, revision: revision + 1 };
      },
      async () => undefined,
    );
  }
  async restoreHistory(
    kind: StudyKind,
    id: string,
    revision: number,
    historyId: string,
    op: string,
  ) {
    return this.command(
      op,
      { kind, id, revision, historyId },
      async () => {
        const old = await this.table(kind).get(id),
          r = await this.db.revisions.get(historyId);
        if (
          !old ||
          old.deletedAt ||
          old.revision !== revision ||
          !r ||
          !("entityType" in r) ||
          r.entityType !== kind ||
          r.entityId !== id
        )
          throw Error("对象或历史已变化");
        let input: Record<string, unknown> = {
          ...r.snapshot,
          ...{
            createdAt: old.createdAt,
            archivedAt: old.archivedAt,
            deletedAt: null,
            revision: revision + 1,
            updatedAt: new Date().toISOString(),
          },
        };
        if (kind === "study-plan") {
          const p = old as StudyPlan;
          input = {
            ...input,
            state: p.state,
            endedAt: p.endedAt,
            result: p.result,
            actionResolutions: p.actionResolutions,
          };
          if (
            p.state === "completed" &&
            "taskIds" in input &&
            (input.taskIds as string[]).some((k) => !p.taskIds.includes(k))
          )
            throw Error("已结束计划不能恢复新增关联行动");
        }
        const item = studySchemas[kind].parse(input) as StudyItem;
        await this.references(item, old);
        await this.table(kind).put(item);
        await this.history(kind, item, "history-restored");
        return { value: item, id, revision: item.revision };
      },
      async (id) => (await this.table(kind).get(id))!,
    );
  }
}
const studyPlanSchemaParse = (v: unknown) =>
  studySchemas["study-plan"].parse(v);
